import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';

interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  variant?: 'outlined' | 'filled';
}

const InputContainer = styled.div<{ fullWidth?: boolean }>`
  display: inline-flex;
  flex-direction: column;
  margin-bottom: var(--space-md);
  position: relative;
  
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
`;

const Label = styled.label`
  font-size: var(--font-sm);
  margin-bottom: var(--space-xs);
  font-weight: 500;
  color: var(--text-secondary);
`;

const IconWrapper = styled.div<{ position?: 'start' | 'end' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);
  
  ${({ position }) => position === 'start' && css`
    left: 12px;
  `}
  
  ${({ position }) => position === 'end' && css`
    right: 12px;
  `}
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input<{
  hasError?: boolean;
  hasStartIcon?: boolean;
  hasEndIcon?: boolean;
  variant?: 'outlined' | 'filled';
}>`
  width: 100%;
  padding: 10px 14px;
  font-size: var(--font-md);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-input);
  background-color: var(--bg-light);
  transition: all var(--transition-fast);
  outline: none;
  font-family: 'Poppins', sans-serif;
  
  &:focus {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
  
  &:disabled {
    background-color: var(--bg-disabled);
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  ${({ hasStartIcon }) =>
    hasStartIcon &&
    css`
      padding-left: 40px;
    `}
    
  ${({ hasEndIcon }) =>
    hasEndIcon &&
    css`
      padding-right: 40px;
    `}
    
  ${({ hasError }) =>
    hasError &&
    css`
      border-color: var(--danger-color);
      
      &:focus {
        box-shadow: 0 0 0 3px var(--danger-light);
      }
    `}
    
  ${({ variant }) =>
    variant === 'filled' &&
    css`
      background-color: var(--bg-subtle);
      border-color: transparent;
      
      &:focus {
        background-color: var(--bg-light);
      }
    `}
`;

const HelperText = styled.span<{ hasError?: boolean }>`
  font-size: var(--font-xs);
  margin-top: var(--space-xs);
  
  ${({ hasError }) =>
    hasError
      ? css`
          color: var(--danger-color);
        `
      : css`
          color: var(--text-tertiary);
        `}
`;

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = false,
      startIcon,
      endIcon,
      variant = 'outlined',
      ...props
    },
    ref
  ) => {
    return (
      <InputContainer fullWidth={fullWidth}>
        {label && <Label>{label}</Label>}
        <InputWrapper>
          {startIcon && (
            <IconWrapper position="start">{startIcon}</IconWrapper>
          )}
          <StyledInput
            ref={ref}
            hasError={!!error}
            hasStartIcon={!!startIcon}
            hasEndIcon={!!endIcon}
            variant={variant}
            {...props}
          />
          {endIcon && (
            <IconWrapper position="end">{endIcon}</IconWrapper>
          )}
        </InputWrapper>
        {(error || helperText) && (
          <HelperText hasError={!!error}>{error || helperText}</HelperText>
        )}
      </InputContainer>
    );
  }
);

TextField.displayName = 'TextField';

export default TextField; 